const isCovered = ref(false);

let coverTl = null;

export function useCoverNavigation() {
    const { $gsap: gsap } = useNuxtApp();
    const { screen, shapeTop, shapeBottom } = usePageLoader();
    const globalStore = useGlobalStore();

    const prefersReducedMotion = () =>
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const getEls = () => ({
        screenEl: screen.value,
        shapeTopEl: shapeTop.value,
        shapeBottomEl: shapeBottom.value
    });

    const prepareCover = () => {
        globalStore.setPageTransitionBeforeComplete(false);
        document.body.classList.add('is-loading');
        document.body.classList.add('hide-pointer-events');

        const { screenEl, shapeTopEl, shapeBottomEl } = getEls();
        if (!screenEl) return null;

        gsap.killTweensOf([screenEl, shapeTopEl, shapeBottomEl].filter(Boolean));

        gsap.set(screenEl, { autoAlpha: 1, yPercent: 100 });
        if (shapeBottomEl) gsap.set(shapeBottomEl, { scaleY: 1 });
        if (shapeTopEl) gsap.set(shapeTopEl, { scaleY: 0 });

        return screenEl;
    };

    const applyCoveredState = () => {
        const { screenEl, shapeBottomEl } = getEls();
        if (screenEl) {
            gsap.set(screenEl, { autoAlpha: 1, yPercent: 0 });
            if (shapeBottomEl) gsap.set(shapeBottomEl, { scaleY: 1 });
        }
        isCovered.value = true;
    };

    const cover = () => {
        if (isCovered.value) return Promise.resolve();

        coverTl?.kill();
        coverTl = null;

        const screenEl = prepareCover();

        if (!screenEl || prefersReducedMotion()) {
            applyCoveredState();
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            coverTl = gsap
                .timeline({
                    onComplete: () => {
                        coverTl = null;
                        isCovered.value = true;
                        resolve();
                    }
                })
                .to(screenEl, {
                    duration: 0.52,
                    yPercent: 0,
                    ease: 'power2.inOut'
                })
                .to({}, { duration: 0.1 });
        });
    };

    const clearPrecover = () => {
        isCovered.value = false;
        coverTl?.kill();
        coverTl = null;
    };

    const resetScrollUnderCover = () => {
        if (!isCovered.value || typeof window === 'undefined') return;

        const { $ScrollTrigger: ScrollTrigger } = useNuxtApp();
        // Kill hero header ScrollTriggers before scrolling — otherwise onEnterBack
        // can force data-header-theme="light" (white menu) onto the next page.
        ScrollTrigger.getAll().forEach((tr) => tr.kill());

        const lenis = globalStore.lenis;
        if (lenis) {
            try {
                lenis.scrollTo(0, { immediate: true, force: true });
                lenis.destroy();
            } catch {
                // Lenis may already be torn down
            }
            globalStore.setLenis(null);
        }

        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        document.body.dataset.scrollDirection = 'init';
        document.body.dataset.scrolled = 'false';

        const scrollState = useState('scroll-state', () => 0);
        scrollState.value = 0;
    };

    const emergencyUncover = () => {
        const { screenEl, shapeTopEl, shapeBottomEl } = getEls();
        coverTl?.kill();
        coverTl = null;
        isCovered.value = false;

        if (screenEl) {
            gsap.set(screenEl, { autoAlpha: 0, yPercent: -100 });
            if (shapeTopEl) gsap.set(shapeTopEl, { scaleY: 0 });
            if (shapeBottomEl) gsap.set(shapeBottomEl, { scaleY: 0 });
        }

        document.body.classList.remove('is-loading');
        document.body.classList.remove('hide-pointer-events');
        globalStore.setPageTransitionComplete(true);
        globalStore.setPageTransitionBeforeComplete(true);
    };

    return {
        isCovered,
        cover,
        clearPrecover,
        resetScrollUnderCover,
        emergencyUncover,
        prepareCover,
        applyCoveredState,
        prefersReducedMotion,
        getEls
    };
}
